import { Injectable } from "@angular/core";
import { Directory, FileInfo } from "@capacitor/filesystem";
import { from, Observable, of } from 'rxjs';
import { concatMap, map, mergeMap, tap, toArray } from 'rxjs/operators';
import { PatchBase } from "./PatchBase";
import { SqlHelper } from '../../sqlite/helpers/sql.helper';
import { StringHelper } from "apps/idl/src/helpers";
import { PlatformService } from "apps/idl/src/services/security";
import { FilesystemService } from "../../filesystem/services/filesystem.service";

@Injectable()
export class Sqlite2ToSqliteEvcoreExtbuildFreePatch extends PatchBase {

	//#region FIELDS

	private static readonly C_LOG_ID = "SQL2toSQLEEF.PATCH::";

	//#endregion

	//#region METHODS

	constructor(
		private readonly isvcPlatform: PlatformService,
		private readonly isvcFilesystem: FilesystemService
	) {
		super("Migration des bases de données sqlite");
	}

	/** @override */
	public applyPatch(): Observable<boolean> {
		const lsSqliteMobileAppDatabasesPath: string = SqlHelper.mobileAppDatabasesPath;

		if (!StringHelper.isBlank(lsSqliteMobileAppDatabasesPath)) {
			console.debug(`${Sqlite2ToSqliteEvcoreExtbuildFreePatch.C_LOG_ID}apply patch for ${this.isvcPlatform.isAndroid ? "android" : this.isvcPlatform.isIOS ? "iOS" : "another platform"} android`);
			return this.innerApplyPatch("", lsSqliteMobileAppDatabasesPath);
		}
		else {
			console.debug(`${Sqlite2ToSqliteEvcoreExtbuildFreePatch.C_LOG_ID}NOT apply patch`);
			return of(true);
		}
	}

	/** Applique le patch de migration des bases de données depuis le plugin sqlite2 vers le plugin sqliteEvcoreExtbuildFree.
	 * @param psOriginalPath Chemin vers les bases de données avec le plugin sqlite2.
	 * @param psNewDatabasesPath Chemin vers les bases de données avec le plugin sqliteEvcoreExtbuildFree.
	 */
	private innerApplyPatch(psOriginalPath: string, psNewDatabasesPath: string): Observable<boolean> {

		return from(this.isvcFilesystem.createDirectoryAsync(psNewDatabasesPath, SqlHelper.mobileAppDatabasesDirectory, false)).pipe(
				mergeMap(_ => this.isvcFilesystem.listDirectoryEntriesAsync(psOriginalPath, SqlHelper.mobileAppDatabasesDirectory).catch(_ => of([]))),
				map((paEntries: FileInfo[]) => paEntries.filter((poEntry: FileInfo) => poEntry.type === "file" && poEntry.name.includes("_core_") && !poEntry.name.endsWith("-journal"))),
				mergeMap((paEntries: FileInfo[]) => this.moveDatabases(paEntries, psNewDatabasesPath)),
			);
	}

	/** Déplace les bases de données depuis le dossier utilisé par le plugin sqlite2 vers le dossier utilisé par le plugin sqliteEvcoreExtbuildFree.
	 * @param paEntries Tableau des dossiers et fichiers présents dans le dossier dont il faut déplacer les bases de données.
	 * @param poLoader Loader affiché qu'il faut mettre à jour au fur-et-à-mesure des déplacements.
	 */
	private moveDatabases(paEntries: FileInfo[], psNewDatabasesPath: string): Observable<boolean> {
		let lnIndex = 0;

		return from(paEntries)
			.pipe(
				concatMap((poFileEntry: FileInfo) => {
					console.info(`${Sqlite2ToSqliteEvcoreExtbuildFreePatch.C_LOG_ID}Migration base de données '${poFileEntry.name}' en cours ...`);

					return this.innerMoveDatabasesAsync(poFileEntry, psNewDatabasesPath);
				}),
				tap(_ => ++lnIndex),
				toArray(),
				map((paResults: boolean[]) => paResults.every((pbResult: boolean) => pbResult))
			);
	}

	private async innerMoveDatabasesAsync(poFileEntry: FileInfo, psNewDatabasesPath: string): Promise<boolean> {
		try {
			// On copie la base vers son nouveau chemin puis on la supprime
			// => cela évite des possibles corruptions de bases de données dans le cas où l'app crasherait pendant un déplacement.
			await this.isvcFilesystem.copyAsync(poFileEntry.name, `${psNewDatabasesPath}${poFileEntry.name}`, Directory.Data, SqlHelper.mobileAppDatabasesDirectory);
			await this.isvcFilesystem.removeAsync(poFileEntry.name, Directory.Data);
			console.info(`${Sqlite2ToSqliteEvcoreExtbuildFreePatch.C_LOG_ID}Migration base de données '${poFileEntry.name}' terminée.`)
			return true;
		}
		catch (poError) {
			console.error(`${Sqlite2ToSqliteEvcoreExtbuildFreePatch.C_LOG_ID}Migration base de données '${poFileEntry.name}' échouée.`);
			return false;
		}
	}
	//#endregion
}
